/**
 * Campaigns and awards (GET/POST/PATCH/DELETE /api/campaigns, /api/activated-awards,
 * /api/finished-awards). Traps handled here (rgs-api-contract api-traps):
 * - #6 `Whitelisted*` on campaigns is validated as an object, so the arrays webapi needs are
 *   rejected: the builder never sends campaign whitelists.
 * - #7 `@IsUUID` wants hyphens while GETs return 32-char hex: `withHyphens()` before sending.
 * - #8 `GET /campaigns/:id` nests paged awards sharing one pager: awards are read from the
 *   award endpoints instead, filtered by `CampaignId`.
 * - #9 filtering campaigns by `OperatorUUID` never matches: no operator filter.
 * - Paged lists turn every raw Date column into `{}` (the server's transformUUIDToHex walks into
 *   Dates), so list dates parse as missing; detail responses carry real ISO strings.
 * - #30 every write flushes the whole shared Redis: no write loops.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { flag, hexId, jsonColumn } from '@/api/zod-helpers';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

// ---- wire helpers ---------------------------------------------------------------------------

/** A timestamp, or null when missing or mangled to `{}` by the paged-list serializer. */
const looseDate = z.unknown().transform((v) => (typeof v === 'string' && v.trim() !== '' ? v : null));
const json = jsonColumn(z.unknown());
const optionalNumber = z.coerce.number().nullable().catch(null);

/** 32-char hex → the hyphenated form `@IsUUID` accepts. */
export function withHyphens(hex: string): string {
  const h = hex.replace(/-/g, '').toLowerCase();
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export const shortId = (hex: string | null | undefined) => (hex ? hex.slice(0, 8) : '—');

// ---- campaigns ------------------------------------------------------------------------------

export const campaignSchema = z.object({
  Id: z.coerce.number(),
  UUID: hexId.nullable(),
  OperatorUUID: hexId.nullable(),
  Type: z.coerce.number(),
  Name: z
    .string()
    .nullable()
    .transform((v) => v ?? ''),
  CampaignMetaData: json,
  PrizeDetails: json,
  StartDate: looseDate,
  EndDate: looseDate,
  UseBefore: looseDate,
  NotificationDate: looseDate,
  Status: optionalNumber,
  ForAllPlayers: flag.nullable().catch(null),
  WhitelistedBrands: json,
  AutoPayout: flag.nullable().catch(null),
  AdditionalData: json,
  Rules: json,
  Notification: json,
  WhitelistedGames: json,
  Currency: z.string().nullable().catch(null),
  ActivatedAwardsCount: z.coerce.number().optional().catch(undefined),
  FinishedAwardsCount: z.coerce.number().optional().catch(undefined),
  // The detail carries the awards as paged envelopes (trap #8); only their totals are read.
  ActivatedAwards: z.object({ total: z.coerce.number() }).optional().catch(undefined),
  FinishedAwards: z.object({ total: z.coerce.number() }).optional().catch(undefined),
});

export type Campaign = z.infer<typeof campaignSchema>;

/** Award counts from a list row or a detail response. */
export const awardCounts = (c: Campaign) => ({
  active: c.ActivatedAwardsCount ?? c.ActivatedAwards?.total ?? 0,
  finished: c.FinishedAwardsCount ?? c.FinishedAwards?.total ?? 0,
});

/** The server's allow-list (module-fields.config.ts `campaigns`). Status and EndDate aren't in it. */
export const CAMPAIGN_SORT_FIELDS = [
  'Id',
  'UUID',
  'OperatorUUID',
  'Type',
  'Name',
  'StartDate',
  'UseBefore',
  'Currency',
] as const;
export const DEFAULT_CAMPAIGN_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

export const campaignsSearchSchema = listSearchSchema({
  sortFields: CAMPAIGN_SORT_FIELDS,
  filters: { type: filterParam.number(), operator: filterParam.string() },
});
export type CampaignsSearch = z.infer<typeof campaignsSearchSchema>;

export function campaignsPagedQuery(search: CampaignsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Name', operator: 'contains', value: search.q });
  if (search.type !== undefined) filters.push({ field: 'Type', operator: 'eq', value: search.type });
  if (search.operator) filters.push({ field: 'OperatorUUID', operator: 'eq', value: search.operator });
  return toPagedQuery(search, { defaultSort: DEFAULT_CAMPAIGN_SORT, filters });
}

export const promotionsKeys = {
  campaigns: ['promotions', 'campaigns'] as const,
  campaignList: (q: PagedQuery) => ['promotions', 'campaigns', 'list', q] as const,
  campaign: (id: number) => ['promotions', 'campaigns', 'detail', id] as const,
  campaignOptions: ['promotions', 'campaigns', 'options'] as const,
  awards: ['promotions', 'awards'] as const,
  awardList: (kind: AwardKind, q: PagedQuery) => ['promotions', 'awards', kind, 'list', q] as const,
  award: (kind: AwardKind, uuid: string) => ['promotions', 'awards', kind, 'detail', uuid] as const,
  operators: ['promotions', 'operator-options'] as const,
  games: ['promotions', 'game-options'] as const,
};

const campaignListSchema = pagedSchema(campaignSchema);

export const campaignsListQuery = (search: CampaignsSearch) => {
  const query = campaignsPagedQuery(search);
  return queryOptions({
    queryKey: promotionsKeys.campaignList(query),
    queryFn: ({ signal }) => http('/campaigns', { query: encodePagedQuery(query), signal }, campaignListSchema),
    placeholderData: keepPreviousData,
  });
};

export const campaignQuery = (id: number) =>
  queryOptions({
    queryKey: promotionsKeys.campaign(id),
    // take=1 keeps the nested award envelopes (trap #8) small; awards come from their own endpoints.
    queryFn: ({ signal }) => http(`/campaigns/${id}`, { query: { take: 1 }, signal }, campaignSchema),
  });

const campaignOptionSchema = z.array(
  z.object({ Id: z.coerce.number(), Name: z.string().nullable(), Type: z.coerce.number().optional() }),
);

export const campaignOptionsQuery = () =>
  queryOptions({
    queryKey: promotionsKeys.campaignOptions,
    queryFn: ({ signal }) => http('/campaigns/options', { signal }, campaignOptionSchema),
    staleTime: 5 * 60_000,
  });

// ---- lookups (small, read-only; features never import each other) -----------------------------

/** Operators as options. Only Id/UUID/Name: the list response also carries `ApiSalt`, which is dropped here. */
const operatorOptionSchema = pagedSchema(z.object({ Id: z.coerce.number(), UUID: hexId, Name: z.string() }));

export const operatorOptionsQuery = () =>
  queryOptions({
    queryKey: promotionsKeys.operators,
    queryFn: ({ signal }) =>
      http(
        '/operators',
        { query: encodePagedQuery({ skip: 0, take: 500, sort: { field: 'Name', dir: 'asc' } }), signal },
        operatorOptionSchema,
      ).then((page) => page.data),
    staleTime: 5 * 60_000,
  });

/** Games as options, to name whitelisted game UUIDs. Only non-secret fields are kept. */
const gameOptionSchema = pagedSchema(z.object({ Id: z.coerce.number(), UUID: hexId, Name: z.string() }));

export const gameOptionsQuery = () =>
  queryOptions({
    queryKey: promotionsKeys.games,
    queryFn: ({ signal }) =>
      http(
        '/games',
        { query: encodePagedQuery({ skip: 0, take: 1000, sort: { field: 'Name', dir: 'asc' } }), signal },
        gameOptionSchema,
      ).then((page) => page.data),
    staleTime: 5 * 60_000,
  });

// ---- campaign writes ------------------------------------------------------------------------

/** What the builder edits; JSON columns are objects (or null) once parsed. */
export interface CampaignValues extends Record<string, unknown> {
  Name: string;
  Type: number;
  OperatorUUID: string;
  Currency: string | null;
  Status: number;
  StartDate: string | null;
  EndDate: string | null;
  UseBefore: string | null;
  NotificationDate: string | null;
  ForAllPlayers: boolean;
  AutoPayout: boolean;
  PrizeDetails: Record<string, unknown> | null;
  Rules: Record<string, unknown> | null;
  Notification: Record<string, unknown> | null;
  AdditionalData: Record<string, unknown> | null;
  CampaignMetaData: Record<string, unknown> | null;
}

const asObject = (v: unknown) =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

export const toCampaignValues = (c: Campaign): CampaignValues => ({
  Name: c.Name,
  Type: c.Type,
  OperatorUUID: c.OperatorUUID ?? '',
  Currency: c.Currency || null,
  Status: c.Status ?? 0,
  StartDate: c.StartDate,
  EndDate: c.EndDate,
  UseBefore: c.UseBefore,
  NotificationDate: c.NotificationDate,
  ForAllPlayers: c.ForAllPlayers ?? false,
  AutoPayout: c.AutoPayout ?? false,
  PrizeDetails: asObject(c.PrizeDetails),
  Rules: asObject(c.Rules),
  Notification: asObject(c.Notification),
  AdditionalData: asObject(c.AdditionalData),
  CampaignMetaData: asObject(c.CampaignMetaData),
});

/** Flags as 0/1, UUIDs hyphenated (trap #7). Whitelists are never sent (trap #6). */
export function toCampaignBody(values: Partial<CampaignValues> & { UUID?: string }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (key === 'ForAllPlayers' || key === 'AutoPayout') body[key] = value ? 1 : 0;
    else if ((key === 'OperatorUUID' || key === 'UUID') && typeof value === 'string') body[key] = withHyphens(value);
    else body[key] = value;
  }
  return body;
}

export function useSaveCampaign(id: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id === null
        ? http('/campaigns', { method: 'POST', body }, campaignSchema)
        : http(`/campaigns/${id}`, { method: 'PATCH', body }, campaignSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.campaigns });
    },
  });
}

export function useDeleteCampaign(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: promotionsKeys.campaign(id) });
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.campaigns });
    },
  });
}

// ---- awards ---------------------------------------------------------------------------------

export type AwardKind = 'active' | 'finished';
const AWARD_PATH: Record<AwardKind, string> = { active: '/activated-awards', finished: '/finished-awards' };

const awardPlayerSchema = z
  .object({
    Id: z.coerce.number(),
    operatorId: optionalNumber.optional(),
    OperatorPlayerId: z.string().nullable().optional(),
    OperatorPlayerNickname: z.string().nullable().optional(),
    Currency: z.string().nullable().optional(),
    Brand: z.string().nullable().optional(),
  })
  .nullable()
  .optional()
  .catch(null);

export const awardSchema = z.object({
  UUID: hexId,
  CampaignId: optionalNumber,
  Status: optionalNumber,
  OperatorPlayerId: z.string().nullable().catch(null),
  AwardData: json,
  CreatedAt: looseDate,
  UpdatedAt: looseDate,
  UseBefore: looseDate,
  Progress: optionalNumber,
  MissionProgress: json,
  AdditionalData: json,
  WhitelistedGames: json,
  Currency: z.string().nullable().catch(null),
  Brand: z.string().nullable().catch(null),
  Player: awardPlayerSchema,
  GameName: z.string().nullable().optional().catch(null),
});

export type Award = z.infer<typeof awardSchema>;

/**
 * The server's allow-list (module-fields.config.ts `awards`), shared by both award lists, minus
 * `Player.OperatorPlayerNickname`: it filters, but sorting on it answers 400 (buildOrder hands
 * TypeORM the dotted name as a flat key).
 */
export const AWARD_SORT_FIELDS = [
  'UUID',
  'CampaignId',
  'Status',
  'OperatorPlayerId',
  'CreatedAt',
  'UseBefore',
  'Brand',
] as const;
export const DEFAULT_AWARD_SORT: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

export const awardsSearchSchema = listSearchSchema({
  sortFields: AWARD_SORT_FIELDS,
  filters: {
    status: filterParam.number(),
    campaign: filterParam.number(),
    player: filterParam.string(),
    brand: filterParam.string(),
    operator: filterParam.string(),
  },
});
export type AwardsSearch = z.infer<typeof awardsSearchSchema>;

export function awardsPagedQuery(search: AwardsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Player.OperatorPlayerNickname', operator: 'contains', value: search.q });
  if (search.player) filters.push({ field: 'OperatorPlayerId', operator: 'eq', value: search.player });
  if (search.status !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.status });
  if (search.campaign !== undefined) filters.push({ field: 'CampaignId', operator: 'eq', value: search.campaign });
  if (search.brand) filters.push({ field: 'Brand', operator: 'eq', value: search.brand });
  // Awards have no operator column: they belong to their campaign's operator
  if (search.operator) filters.push({ field: 'Campaign.OperatorUUID', operator: 'eq', value: search.operator });
  return toPagedQuery(search, { defaultSort: DEFAULT_AWARD_SORT, filters });
}

const awardListSchema = pagedSchema(awardSchema);

export const awardsListQuery = (kind: AwardKind, search: AwardsSearch) => {
  const query = awardsPagedQuery(search);
  return queryOptions({
    queryKey: promotionsKeys.awardList(kind, query),
    queryFn: ({ signal }) => http(AWARD_PATH[kind], { query: encodePagedQuery(query), signal }, awardListSchema),
    placeholderData: keepPreviousData,
  });
};

export const awardQuery = (kind: AwardKind, uuid: string) =>
  queryOptions({
    queryKey: promotionsKeys.award(kind, uuid),
    queryFn: ({ signal }) => http(`${AWARD_PATH[kind]}/${encodeURIComponent(uuid)}`, { signal }, awardSchema),
  });

/** Cancelling an active award deletes it; a database trigger archives it to finished awards. */
export function useCancelAward(uuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => http(`${AWARD_PATH.active}/${encodeURIComponent(uuid)}`, { method: 'DELETE' }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: promotionsKeys.award('active', uuid) });
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.awards });
    },
  });
}

// ---- grant award ----------------------------------------------------------------------------

const playerOptionSchema = pagedSchema(
  z.object({
    Id: z.coerce.number(),
    operatorId: z.coerce.number(),
    OperatorPlayerId: z.string(),
    OperatorPlayerNickname: z.string().nullable(),
    Currency: z.string().nullable(),
    Brand: z.string().nullable(),
    IsTestPlayer: flag.catch(false),
  }),
);
export type PlayerOption = z.infer<typeof playerOptionSchema>['data'][number];

/** Players of one operator, found by nickname or operator player id (the server checks the operator too). */
export const playerSearchQuery = (operatorId: number, text: string) =>
  queryOptions({
    queryKey: ['promotions', 'player-search', operatorId, text] as const,
    queryFn: async ({ signal }) => {
      const base: FilterDescriptor = { field: 'operatorId', operator: 'eq', value: operatorId };
      const byId = http(
        '/players',
        {
          query: encodePagedQuery({
            skip: 0,
            take: 10,
            filters: [base, { field: 'OperatorPlayerId', operator: 'eq', value: text }],
          }),
          signal,
        },
        playerOptionSchema,
      );
      const byName = http(
        '/players',
        {
          query: encodePagedQuery({
            skip: 0,
            take: 10,
            filters: [base, { field: 'OperatorPlayerNickname', operator: 'contains', value: text }],
          }),
          signal,
        },
        playerOptionSchema,
      );
      const [a, b] = await Promise.all([byId, byName]);
      const seen = new Set<number>();
      return [...a.data, ...b.data].filter((p) => (seen.has(p.Id) ? false : (seen.add(p.Id), true)));
    },
    enabled: text.trim().length >= 2,
  });

const gameConfigsSchema = z.object({
  data: z.array(
    z.object({
      currency: z.string(),
      games: z.array(
        z.object({
          gameId: z.coerce.number(),
          gameName: z.string().nullable().optional(),
          gameConfigs: z
            .object({
              gameConfigs: z
                .object({
                  betConfig: z
                    .object({
                      min: z.coerce.number().optional(),
                      max: z.coerce.number().optional(),
                      default: z.coerce.number().optional(),
                      step: z.coerce.number().optional(),
                    })
                    .partial()
                    .optional(),
                })
                .partial()
                .optional(),
              availableBetAmounts: z.array(z.coerce.number()).optional().catch([]),
            })
            .partial()
            .optional(),
        }),
      ),
    }),
  ),
});

export interface BetLadder {
  /** Discrete stakes in cents of the player's currency, when the game has a ladder. */
  amounts: number[];
  min: number | null;
  max: number | null;
  default: number | null;
  /** Stake increment for range games, in cents; null when the game declares none. */
  step: number | null;
}

/** The bets a game allows for one operator, brand and currency, as the server resolves them. */
export const betLadderQuery = (operatorId: number, brand: string, gameId: number, currency: string) =>
  queryOptions({
    queryKey: ['promotions', 'bet-ladder', operatorId, brand, gameId, currency] as const,
    queryFn: async ({ signal }): Promise<BetLadder> => {
      const res = await http(
        `/configs/game-configs/${operatorId}/${encodeURIComponent(brand)}/${gameId}/${encodeURIComponent(currency)}`,
        { signal },
        gameConfigsSchema,
      );
      const game = res.data.find((d) => d.currency === currency)?.games.find((g) => g.gameId === gameId);
      const bet = game?.gameConfigs?.gameConfigs?.betConfig;
      return {
        amounts: game?.gameConfigs?.availableBetAmounts ?? [],
        min: bet?.min ?? null,
        max: bet?.max ?? null,
        default: bet?.default ?? null,
        step: bet?.step ?? null,
      };
    },
  });

export interface GrantInput {
  /** One per dialog session, so a retried submit is recognised as the same grant (T092). */
  uuid: string;
  campaign: Campaign;
  player: PlayerOption;
  gameUuid: string;
  /** Cents of the player's currency. */
  betAmount: number;
  spins: number;
  useBefore: string | null;
}

/**
 * The award body. `AwardData` carries the stake as top-level `amount` (the canonical field new grants
 * must store) and as `bet.amount` (the shape older webapi readers expect), both in cents of the
 * player's currency. The UUID comes from the dialog, one per grant: a free-bet grant goes through the
 * game server, which names the award itself and uses this UUID to recognise a retried submit of a
 * flexible award (T092); other campaign types still store it as the primary key.
 */
export function toAwardBody(input: GrantInput): Record<string, unknown> {
  return {
    UUID: input.uuid,
    CampaignId: input.campaign.Id,
    OperatorPlayerId: input.player.OperatorPlayerId,
    Status: 1,
    AwardData: {
      amount: input.betAmount,
      bet: { amount: input.betAmount },
      count: input.spins,
      total: input.spins,
    },
    UseBefore: input.useBefore ?? undefined,
    WhitelistedGames: [input.gameUuid],
    Currency: input.player.Currency ?? undefined,
    Brand: input.player.Brand ?? undefined,
  };
}

export interface FreeSpinsInput {
  /** One per grant, so a retried submit answers the same award (T092). */
  requestId: string;
  /** Sent only by cross-operator staff; an operator-scoped user always grants for their own operator. */
  operatorUuid: string | null;
  player: PlayerOption;
  gameUuid: string;
  /** Cents of the player's currency. */
  betAmount: number;
  spins: number;
  useBefore: string;
}

/** Free spins with no campaign (T115): the game server files them under the operator's flexible campaign. */
export function toFreeSpinsBody(input: FreeSpinsInput): Record<string, unknown> {
  return {
    ...(input.operatorUuid ? { OperatorUUID: input.operatorUuid } : {}),
    OperatorPlayerId: input.player.OperatorPlayerId,
    GameUUID: input.gameUuid,
    Currency: input.player.Currency ?? undefined,
    Brand: input.player.Brand ?? undefined,
    Spins: input.spins,
    Amount: input.betAmount,
    UseBefore: input.useBefore,
    RequestId: input.requestId,
  };
}

export function useGrantAward() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => http(AWARD_PATH.active, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.awards });
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.campaigns });
    },
    // A failed grant may still have been stored (a 5xx or timeout after webapi wrote it): show the list as it is
    onError: () => queryClient.invalidateQueries({ queryKey: promotionsKeys.awards }),
  });
}

export function useGrantFreeSpins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => http(`${AWARD_PATH.active}/free-spins`, { method: 'POST', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.awards });
      // The first grant may have created the operator's flexible campaign
      await queryClient.invalidateQueries({ queryKey: promotionsKeys.campaigns });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: promotionsKeys.awards }),
  });
}
